import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";

/**
 * Format-keyed DataSource Writer for CSV. Loads a staged CSV file into the
 * DataSource Link's ingest table, applying the Link's parse config (delimiter,
 * quote, encoding + column mapping) in a single pass. The format/mapping comes
 * from the Link's `parse_config` (which must be `csv`), not from the socket —
 * pair this with the connection-keyed adapter that staged the file.
 */
export const csvDataSourceWriterStep = defineStepConfig({
  type: "csv_datasource_writer",
  name: "Csv DataSource Writer",
  description:
    "Load a staged CSV file into a DataSource Link's data sink, applying the Link's CSV parse config (mapping + options).",
  category: "DataSource Writer",

  experimental: {
    reason:
      "Csv DataSource Writer is under active development and not yet validated for production workflows.",
  },

  inputSchema: z.object({
    blobStorageUri: z.string().describe("URI of the source CSV file to load"),
    dataSourceLink: z
      .string()
      .uuid()
      .describe(
        "DataSource Link identifying the source connection + credential, the CSV parse config, and the target schema + table",
      ),
    resumeRowOffset: z
      .number()
      .int()
      .nonnegative()
      .optional()
      .describe(
        "Manually resume from this row instead of row 0 — paste the `resumeRowOffset` from a failed run's last heartbeat details when starting a fresh rerun after all retries were exhausted. Ignored when this attempt already has its own heartbeat checkpoint (same-execution automatic retry).",
      ),
  }),

  outputSchema: z.object({
    rowCount: z.number().describe("Rows successfully upserted"),
    failedCount: z
      .number()
      .describe("Rows that failed and were dead-lettered to .failed.csv"),
  }),

  configSchema: z.object({
    batchSize: z
      .number()
      .int()
      .positive()
      .prefault(500)
      .describe("Number of rows per SQL insert batch"),
    skipUnchangedRows: z
      .boolean()
      .prefault(false)
      .describe(
        "Leave a conflicting row untouched when every value already matches, instead of rewriting it. Cuts WAL and downstream CDC traffic on re-ingests of unchanged data. Off by default: it stops refreshing updated_at on unchanged rows, so that column comes to mean 'when this row last changed' rather than 'when the source last asserted it' — do not enable it for a table whose readers rely on the latter. Automatically ignored for a table with a column type the guard cannot compare exactly.",
      ),
    exclusiveLoad: z
      .boolean()
      .prefault(false)
      .describe(
        "Backfill mode. Loads into an EMPTY destination table with its secondary indexes dropped, then rebuilds them, in one transaction. Measured on a prototype at 9.2x faster publish and 2.8x faster overall. Only for an initial load or a re-baseline into a fresh table: it verifies the destination is empty under an exclusive lock and silently falls back to the ordinary path if it is not, it blocks other writers for the whole load, and it has NO mid-load resume — a failure rolls the load back and a retry starts from row zero. Unique and primary-key constraints are kept, so a duplicate key inside the load is dead-lettered rather than collapsed.",
      ),
  }),

  emits: [] as const,

  // Hard-coded fanout zip (size 1 → one file per run): the connection-keyed
  // adapter emits index-aligned blobStorageUris + dataSourceLinks arrays into
  // this writer's two single-value sockets. `batch` makes the engine lockstep-zip
  // them (one URI + its own link per run) instead of cross-producing. Fixed on
  // the step type, so it's not an editable config field and never shows in the UI.
  batch: { size: 1 },

  execution: {
    // Generous ceiling per attempt: a legitimate load can run long (multi-million
    // row files, ingest-pool contention from parallel writers, dirty files that
    // degrade to bisected retries). Liveness is enforced by the 2-minute
    // heartbeatTimeout + resume checkpoint, not this cap.
    startToCloseTimeout: "4 hours",
    // Deploys restart workers frequently, and each restart looks like a dead
    // activity to Temporal (heartbeat timeout) — a routine event, not a
    // processing failure. A high ceiling lets a long file load survive many
    // redeploys across its run; each retry resumes from the last committed
    // checkpoint instead of restarting at row 0, so extra attempts are cheap.
    // `maximumInterval` caps backoff growth so a retry after a worker restart
    // reschedules quickly rather than waiting an ever-longer interval.
    retry: {
      maximumAttempts: 8,
      backoffCoefficient: 2,
      initialInterval: "10 seconds",
      maximumInterval: "1 minute",
    },
  },
});
